import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import axios from "axios";

// Mirrors PlayerCardStatsBuilder::DEFAULT_CONFIG — used to fill any gaps in the
// saved config so the sliders always have a value to bind to.
const DEFAULTS = {
  generosity: 0,
  defender_boost_max: 10,
  gaa_good: 3.0,
  gaa_bad: 9.0,
  goalie_goal_bonus: 8,
  goalie_assist_bonus: 4,
  goalie_offense_cap: 15,
  tiers: { silver: 65, gold: 80, platinum: 90 },
  weights: {
    forward: { goals: 50, assists: 30, availability: 20 },
    defence: { goals: 15, assists: 45, availability: 40 },
    goaltender: { goals_against: 70, availability: 30 },
  },
};

const GROUPS = [
  { key: "forward", stats: ["goals", "assists", "availability"] },
  { key: "defence", stats: ["goals", "assists", "availability"] },
  { key: "goaltender", stats: ["goals_against", "availability"] },
];

const RARITIES = ["platinum", "gold", "silver", "bronze"];

const withDefaults = (cfg) => ({
  generosity: cfg.generosity ?? DEFAULTS.generosity,
  defender_boost_max: cfg.defender_boost_max ?? DEFAULTS.defender_boost_max,
  gaa_good: cfg.gaa_good ?? DEFAULTS.gaa_good,
  gaa_bad: cfg.gaa_bad ?? DEFAULTS.gaa_bad,
  goalie_goal_bonus: cfg.goalie_goal_bonus ?? DEFAULTS.goalie_goal_bonus,
  goalie_assist_bonus: cfg.goalie_assist_bonus ?? DEFAULTS.goalie_assist_bonus,
  goalie_offense_cap: cfg.goalie_offense_cap ?? DEFAULTS.goalie_offense_cap,
  tiers: { ...DEFAULTS.tiers, ...(cfg.tiers || {}) },
  weights: {
    forward: { ...DEFAULTS.weights.forward, ...((cfg.weights || {}).forward || {}) },
    defence: { ...DEFAULTS.weights.defence, ...((cfg.weights || {}).defence || {}) },
    goaltender: { ...DEFAULTS.weights.goaltender, ...((cfg.weights || {}).goaltender || {}) },
  },
});

const CardScoringSettings = ({ cardScoring, setCardScoring, seasons = [], t_controls }) => {
  const t = t_controls.card_scoring || {};
  const cfg = useMemo(() => withDefaults(cardScoring || {}), [cardScoring]);

  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState(false);
  // Default to the first season that actually has games, so the preview isn't
  // empty when the active season hasn't started yet.
  const [seasonId, setSeasonId] = useState(() => {
    const withGames = seasons.find((s) => s.games > 0);
    return String((withGames || seasons[0] || {}).id || "");
  });
  const debounceRef = useRef(null);

  const label = (key, fallback) => (t[key] != null ? t[key] : fallback);

  const setWeight = (group, stat, value) => {
    setCardScoring({
      ...cfg,
      weights: { ...cfg.weights, [group]: { ...cfg.weights[group], [stat]: value } },
    });
  };
  const setTier = (tier, value) =>
    setCardScoring({ ...cfg, tiers: { ...cfg.tiers, [tier]: value } });
  const setField = (field, value) => setCardScoring({ ...cfg, [field]: value });

  // Numeric <input> with the same edit-then-clamp-on-blur behaviour as the tier
  // inputs: ignore empty/non-numeric while typing, clamp into range on blur.
  const parseNum = (raw, float) => (float ? parseFloat(raw) : parseInt(raw, 10));
  const numInput = (field, { min, max, step }) => {
    const float = step != null && step < 1;
    return (
      <input
        type="number"
        className="form-control"
        min={min}
        max={max}
        step={step || 1}
        value={cfg[field]}
        onChange={(e) => {
          const v = parseNum(e.target.value, float);
          if (!Number.isNaN(v)) setField(field, v);
        }}
        onBlur={(e) => {
          const v = parseNum(e.target.value, float);
          setField(field, Number.isNaN(v) ? cfg[field] : Math.min(max, Math.max(min, v)));
        }}
      />
    );
  };

  // Debounced live preview: re-tier the current roster with the unsaved config.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const locale = window.location.pathname.split("/")[1];
      axios
        .post(
          `/${locale}/settings/card_scoring_preview`,
          { card_scoring: JSON.stringify(cfg), season_id: seasonId },
          { headers: { "X-CSRF-TOKEN": document.querySelector("[name=csrf-token]").content } }
        )
        .then((res) => {
          setPreview(res.data);
          setPreviewError(false);
        })
        .catch(() => setPreviewError(true));
    }, 400);
    return () => clearTimeout(debounceRef.current);
  }, [cfg, seasonId]);

  return (
    <div className="card mt-3">
      <div className="card-body">
        <p className="text-muted small">
          {label(
            "description",
            "Tune how player trading-card ratings are calculated. Weights set how much each stat matters per position; generosity shifts every rating up or down; tier cutoffs set the bronze/silver/gold/platinum thresholds."
          )}
        </p>

        {/* Per-position weight sliders */}
        {GROUPS.map((group) => (
          <div className="form-group mt-3" key={group.key}>
            <label className="mb-1">
              <strong>{label(group.key, group.key)}</strong>
            </label>
            {group.stats.map((stat) => (
              <div className="d-flex align-items-center mb-1" key={stat}>
                <span className="small" style={{ width: 130 }}>
                  {label(stat, stat)}
                </span>
                <input
                  type="range"
                  className="form-control-range flex-grow-1 mx-2"
                  min="0"
                  max="100"
                  value={cfg.weights[group.key][stat]}
                  onChange={(e) => setWeight(group.key, stat, parseInt(e.target.value, 10) || 0)}
                />
                <span className="small text-muted" style={{ width: 32, textAlign: "right" }}>
                  {cfg.weights[group.key][stat]}
                </span>
              </div>
            ))}
          </div>
        ))}

        {/* Generosity + defender boost */}
        <div className="form-group mt-3">
          <div className="d-flex align-items-center mb-1">
            <span className="small" style={{ width: 130 }}>{label("generosity", "Generosity")}</span>
            <input
              type="range"
              className="form-control-range flex-grow-1 mx-2"
              min="-20"
              max="20"
              value={cfg.generosity}
              onChange={(e) => setField("generosity", parseInt(e.target.value, 10) || 0)}
            />
            <span className="small text-muted" style={{ width: 32, textAlign: "right" }}>
              {cfg.generosity > 0 ? `+${cfg.generosity}` : cfg.generosity}
            </span>
          </div>
          <div className="d-flex align-items-center mb-1">
            <span className="small" style={{ width: 130 }}>
              {label("defender_boost_max", "Defender boost (max)")}
            </span>
            <input
              type="range"
              className="form-control-range flex-grow-1 mx-2"
              min="0"
              max="15"
              value={cfg.defender_boost_max}
              onChange={(e) => setField("defender_boost_max", parseInt(e.target.value, 10) || 0)}
            />
            <span className="small text-muted" style={{ width: 32, textAlign: "right" }}>
              +{cfg.defender_boost_max}
            </span>
          </div>
          <small className="form-text text-muted">
            {label(
              "defender_boost_help",
              "Defenders gain up to this many rating points based on how many of the team's games they played."
            )}
          </small>
        </div>

        {/* Tier cutoffs */}
        <div className="form-group mt-3">
          <label className="mb-1"><strong>{label("tiers", "Tier cutoffs (min OVR)")}</strong></label>
          <div className="d-flex">
            {["silver", "gold", "platinum"].map((tier) => (
              <div className="mr-3" key={tier}>
                <label className="small mb-0 text-capitalize">{label(tier, tier)}</label>
                <input
                  type="number"
                  className="form-control"
                  min="40"
                  max="99"
                  value={cfg.tiers[tier]}
                  onChange={(e) => {
                    // Ignore empty/non-numeric input while editing so a cleared
                    // field never collapses to 0; clamping happens on blur.
                    const v = parseInt(e.target.value, 10);
                    if (!Number.isNaN(v)) setTier(tier, v);
                  }}
                  onBlur={(e) => {
                    const v = parseInt(e.target.value, 10);
                    setTier(tier, Number.isNaN(v) ? cfg.tiers[tier] : Math.min(99, Math.max(40, v)));
                  }}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Goaltender scoring: GAA band + offensive bonus */}
        <div className="form-group mt-3">
          <label className="mb-1"><strong>{label("goaltender_scoring", "Goaltender scoring")}</strong></label>
          <div className="d-flex">
            <div className="mr-3" style={{ maxWidth: 110 }}>
              <label className="small mb-0">{label("gaa_good", "GAA: full score")}</label>
              {numInput("gaa_good", { min: 0, max: 8, step: 0.5 })}
            </div>
            <div className="mr-3" style={{ maxWidth: 110 }}>
              <label className="small mb-0">{label("gaa_bad", "GAA: zero score")}</label>
              {numInput("gaa_bad", { min: 1, max: 15, step: 0.5 })}
            </div>
          </div>
          <small className="form-text text-muted">
            {label(
              "gaa_help",
              "Goals-against average at or below the first value scores full marks; at or above the second scores zero. Lower GAA is better."
            )}
          </small>
          <div className="d-flex mt-2">
            <div className="mr-3" style={{ maxWidth: 110 }}>
              <label className="small mb-0">{label("goalie_goal_bonus", "Bonus / goal")}</label>
              {numInput("goalie_goal_bonus", { min: 0, max: 25, step: 1 })}
            </div>
            <div className="mr-3" style={{ maxWidth: 110 }}>
              <label className="small mb-0">{label("goalie_assist_bonus", "Bonus / assist")}</label>
              {numInput("goalie_assist_bonus", { min: 0, max: 25, step: 1 })}
            </div>
            <div className="mr-3" style={{ maxWidth: 110 }}>
              <label className="small mb-0">{label("goalie_offense_cap", "Bonus cap")}</label>
              {numInput("goalie_offense_cap", { min: 0, max: 40, step: 1 })}
            </div>
          </div>
          <small className="form-text text-muted">
            {label(
              "goalie_offense_help",
              "Extra rating points added on top when a goaltender scores goals or assists, up to the cap. Skaters are unaffected."
            )}
          </small>
        </div>

        {/* Live preview */}
        <div className="mt-3 p-2 border rounded bg-light">
          <div className="d-flex align-items-center justify-content-between">
            <strong className="small">{label("preview", "Live preview")}</strong>
            {seasons.length > 0 && (
              <select
                className="card-scoring-season-select"
                value={seasonId}
                onChange={(e) => setSeasonId(e.target.value)}
              >
                {seasons.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                    {s.games ? ` (${s.games})` : ""}
                  </option>
                ))}
              </select>
            )}
          </div>
          {previewError && (
            <div className="small text-danger">{label("preview_error", "Could not load preview.")}</div>
          )}
          {preview && preview.players && preview.players.length === 0 && (
            <div className="small text-muted mt-1">
              {label("preview_empty", "No players with stats in this season.")}
            </div>
          )}
          {preview && (
            <>
              <div className="small mt-1">
                {RARITIES.map((r) => (
                  <span className="mr-3 text-capitalize" key={r}>
                    {label(r, r)}: <strong>{preview.counts[r] || 0}</strong>
                  </span>
                ))}
              </div>
              <div style={{ maxHeight: 220, overflowY: "auto" }} className="mt-2">
                <table className="table table-sm small mb-0">
                  <tbody>
                    {preview.players.map((p, i) => (
                      <tr key={i}>
                        <td>{p.name}</td>
                        <td className="text-muted">{p.role}</td>
                        <td className="text-right">{p.overall}</td>
                        <td className="text-capitalize">{label(p.rarity, p.rarity)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

CardScoringSettings.propTypes = {
  cardScoring: PropTypes.object.isRequired,
  setCardScoring: PropTypes.func.isRequired,
  seasons: PropTypes.array,
  t_controls: PropTypes.object.isRequired,
};

export default CardScoringSettings;
